/** Zod request schemas shared by API (validation) and client (types). */
import { z } from "zod";
import {
  DECISION_STATUS,
  EVIDENCE_RELATION,
  EVIDENCE_STATE,
  EXTERNAL_REF_KIND,
  HYPOTHESIS_STATE,
  MANUSCRIPT_STATUS,
  MANUSCRIPT_TYPE,
  MEETING_TYPE,
  NOTE_KIND,
  READING_COVERAGE,
  READING_STATUS,
  RECORD_LINK_RELATION,
  RESULT_OUTCOME,
  REVIEW_DECISION,
  REVISION_CHANGE_STATUS,
  SOURCE_TYPE,
  STARTING_TEMPLATE,
  TASK_KIND,
  TASK_STATUS,
  WORKSPACE_KIND,
} from "./constants.js";
import { isDateOnly } from "./dates.js";

/** UUIDs are compared as text in places (owners, preferences): always lowercase them. */
export const zId = z.guid().transform((s) => s.toLowerCase());
export const zDateOnly = z.string().refine(isDateOnly, "Expected a date as YYYY-MM-DD");
const optText = (max = 20_000) => z.string().max(max).nullable().optional();
const zUrlish = z
  .string()
  .max(2000)
  .refine((v) => v === "" || /^(https?:\/\/|doi:|urn:|s3:\/\/|gs:\/\/|file:\/\/|ssh:\/\/|git@)/i.test(v), "Use a full URL (https://…) or identifier scheme");

export const CreateWorkspaceInput = z.object({
  kind: z.enum(WORKSPACE_KIND.values).default("project"),
  name: z.string().trim().min(1).max(200),
  startingTemplate: z.enum(STARTING_TEMPLATE.values).default("blank"),
});

/** Permanent deletion must be confirmed by typing the exact name. */
export const DeleteWorkspaceInput = z.object({
  confirmName: z.string().max(200),
});

export const UpdateWorkspaceInput = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  externalProcessingDisabled: z.boolean().optional(),
});

export const CreateFolderInput = z.object({
  workspaceId: zId,
  parentId: zId.nullable().optional(),
  name: z.string().trim().min(1).max(200),
});

export const UpdateFolderInput = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  parentId: zId.nullable().optional(),
});

export const CreateNoteInput = z.object({
  workspaceId: zId,
  folderId: zId.nullable().optional(),
  title: z.string().trim().max(300).optional(),
  kind: z.enum(NOTE_KIND.values).default("note"),
  templateKey: z.string().max(100).nullable().optional(),
  body: z.string().max(2_000_000).optional(),
  tags: z.array(z.string().trim().min(1).max(60)).max(50).optional(),
});

export const UpdateNoteInput = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  tags: z.array(z.string().trim().min(1).max(60)).max(50).optional(),
  pinned: z.boolean().optional(),
  folderId: zId.nullable().optional(),
  kind: z.enum(NOTE_KIND.values).optional(),
  metaVersion: z.number().int().optional(),
});

export const MoveNoteInput = z.object({
  workspaceId: zId,
  folderId: zId.nullable().optional(),
  relatedAction: z.enum(["move_related", "keep_related", "cancel"]).default("move_related"),
  acknowledgeVisibility: z.boolean().default(false),
});

export const ReproducibilityDetails = z.object({
  repositoryUrl: zUrlish.optional(),
  commit: z.string().max(200).optional(),
  environmentLink: zUrlish.optional(),
  softwareVersions: z.string().max(5000).optional(),
  datasetId: z.string().max(500).optional(),
  datasetVersion: z.string().max(200).optional(),
  datasetChecksum: z.string().max(200).optional(),
  parameters: z.string().max(5000).optional(),
  seed: z.string().max(200).optional(),
  scriptLink: zUrlish.optional(),
  outputLinks: z.string().max(5000).optional(),
});

export const RecordDetails = z
  .object({
    repro: ReproducibilityDetails.optional(),
    tags: z.array(z.string()).optional(),
    /** Progress note: selected date range and included records. */
    rangeFrom: zDateOnly.optional(),
    rangeTo: zDateOnly.optional(),
    includedRefs: z.array(z.object({ type: z.string(), id: zId })).optional(),
    /** Decision: one-line chosen option shown in lists (the full text lives in the note). */
    chosenOption: z.string().max(500).optional(),
  })
  .passthrough();

export const UpdateRecordInput = z.object({
  version: z.number().int(),
  recordDate: zDateOnly.nullable().optional(),
  responsibleUserId: zId.nullable().optional(),
  responsibleText: optText(300),
  resultOutcome: z.enum(RESULT_OUTCOME.values).nullable().optional(),
  studyPhase: z.enum(["plan", "execution"]).nullable().optional(),
  planNoteId: zId.nullable().optional(),
  meetingType: z.enum(MEETING_TYPE.values).nullable().optional(),
  participantMemberIds: z.array(zId).max(100).optional(),
  participantsExternal: z.array(z.string().trim().min(1).max(200)).max(100).optional(),
  libraryItemId: zId.nullable().optional(),
  readingCoverage: z.enum(READING_COVERAGE.values).nullable().optional(),
  details: RecordDetails.optional(),
});

export const StateChangeInput = z.object({
  field: z.enum(["hypothesis_state", "evidence_state", "decision_status"]),
  to: z.string(),
  effectiveDate: zDateOnly.nullable().optional(),
  reason: z.string().max(5000).optional(),
  linkedNoteIds: z.array(zId).max(50).default([]),
  supersededByNoteId: zId.nullable().optional(),
  expectedFrom: z.string().nullable().optional(),
});

export const STATE_FIELD_VALUES = {
  hypothesis_state: HYPOTHESIS_STATE.values as readonly string[],
  evidence_state: EVIDENCE_STATE.values as readonly string[],
  decision_status: DECISION_STATUS.values as readonly string[],
};

export const CreateRecordLinkInput = z.object({
  fromNoteId: zId,
  toType: z.enum(["note", "paper", "task", "manuscript_section", "manuscript"]),
  toId: zId,
  relation: z.enum(RECORD_LINK_RELATION.values),
  note: z.string().max(2000).optional(),
});

export const CreateEvidenceLinkInput = z.object({
  claimNoteId: zId,
  claimQuote: z.string().trim().min(1).max(5000),
  relation: z.enum(EVIDENCE_RELATION.values),
  explanation: z.string().trim().min(1, "Explain how the source relates to the claim").max(5000),
  sourceType: z.enum(["paper", "note"]),
  sourceId: zId,
  sourceAttachmentId: zId.nullable().optional(),
  sourcePage: z.number().int().positive().nullable().optional(),
  sourceSection: z.string().max(300).nullable().optional(),
  sourceQuote: z.string().max(5000).default(""),
});

export const CreateExternalRefInput = z.object({
  ownerType: z.enum(["note", "library_item", "manuscript"]),
  ownerId: zId,
  kind: z.enum(EXTERNAL_REF_KIND.values),
  label: z.string().trim().min(1).max(300),
  url: zUrlish.nullable().optional(),
  identifier: z.string().max(500).nullable().optional(),
  version: z.string().max(200).nullable().optional(),
  checksum: z.string().max(300).nullable().optional(),
  notes: z.string().max(5000).nullable().optional(),
});

export const AuthorSchema = z.object({
  family: z.string().max(300).optional(),
  given: z.string().max(300).optional(),
  literal: z.string().max(500).optional(),
});
export type Author = z.infer<typeof AuthorSchema>;

const libraryItemFields = {
  sourceType: z.enum(SOURCE_TYPE.values),
  title: z.string().max(2000),
  authors: z.array(AuthorSchema).max(500),
  year: z.number().int().min(1000).max(3000).nullable().optional(),
  doi: z.string().max(300).nullable().optional(),
  url: z.string().max(2000).nullable().optional(),
  venue: z.string().max(1000).nullable().optional(),
  volume: z.string().max(100).nullable().optional(),
  issue: z.string().max(100).nullable().optional(),
  pages: z.string().max(100).nullable().optional(),
  publisher: z.string().max(500).nullable().optional(),
  abstract: z.string().max(50_000).nullable().optional(),
  tags: z.array(z.string().trim().min(1).max(60)).max(50),
  citationKey: z
    .string()
    .trim()
    .max(200)
    .regex(/^[A-Za-z0-9_:.\-+/]*$/, "Citation keys may contain letters, digits and _:.-+/")
    .nullable()
    .optional(),
};

export const LibraryItemInput = z.object({
  ...libraryItemFields,
  sourceType: libraryItemFields.sourceType.default("article"),
  title: libraryItemFields.title.default(""),
  authors: libraryItemFields.authors.default([]),
  tags: libraryItemFields.tags.default([]),
});

/** No defaults here: a PATCH must only touch the fields the client sent. */
export const UpdateLibraryItemInput = z
  .object(libraryItemFields)
  .partial()
  .extend({
    readingStatus: z.enum(READING_STATUS.values).optional(),
    reviewDecision: z.enum(REVIEW_DECISION.values).nullable().optional(),
    reviewReason: z.string().max(5000).nullable().optional(),
    version: z.number().int(),
  });

export const BibImportPreviewInput = z.object({
  format: z.enum(["bibtex", "ris"]),
  text: z.string().min(1).max(5_000_000),
  filename: z.string().max(300).optional(),
});

export const BibImportCommitInput = BibImportPreviewInput.extend({
  decisions: z.array(
    z.object({
      index: z.number().int().min(0),
      action: z.enum(["import", "skip", "merge"]),
      mergeIntoId: zId.optional(),
      citationKey: z.string().max(200).nullable().optional(),
    }),
  ),
});

export const MergeLibraryInput = z.object({
  primaryId: zId,
  secondaryId: zId,
  fieldChoices: z.record(z.string(), z.enum(["primary", "secondary"])).default({}),
});

export const CreateTaskInput = z.object({
  workspaceId: zId,
  title: z.string().trim().min(1).max(500),
  description: z.string().max(20_000).default(""),
  status: z.enum(TASK_STATUS.values).default("backlog"),
  kind: z.enum(TASK_KIND.values).default("task"),
  assigneeId: zId.nullable().optional(),
  dueDate: zDateOnly.nullable().optional(),
  blockedReason: z.string().max(2000).nullable().optional(),
  sourceType: z.enum(["note", "revision_item"]).nullable().optional(),
  sourceId: zId.nullable().optional(),
  sourceQuote: z.string().max(5000).nullable().optional(),
  links: z.array(z.object({ targetType: z.enum(["note", "manuscript_section", "manuscript", "paper"]), targetId: zId })).max(20).default([]),
  idempotencyKey: z.string().max(200).optional(),
});

export const UpdateTaskInput = z.object({
  version: z.number().int(),
  title: z.string().trim().min(1).max(500).optional(),
  description: z.string().max(20_000).optional(),
  status: z.enum(TASK_STATUS.values).optional(),
  kind: z.enum(TASK_KIND.values).optional(),
  assigneeId: zId.nullable().optional(),
  dueDate: zDateOnly.nullable().optional(),
  blockedReason: z.string().max(2000).nullable().optional(),
  position: z.number().optional(),
});

export const CreateManuscriptInput = z.object({
  workspaceId: zId,
  title: z.string().trim().min(1).max(500),
  type: z.enum(MANUSCRIPT_TYPE.values).default("article"),
  targetVenue: z.string().max(500).nullable().optional(),
  /** Section titles to start with (default: the usual sections for the type). An abstract is always added. */
  sections: z.array(z.string().trim().min(1).max(300)).max(40).optional(),
});

export const UpdateManuscriptInput = z.object({
  version: z.number().int(),
  title: z.string().trim().min(1).max(500).optional(),
  type: z.enum(MANUSCRIPT_TYPE.values).optional(),
  targetVenue: z.string().max(500).nullable().optional(),
  guidelinesMd: z.string().max(200_000).optional(),
  externalUrl: z.string().max(2000).nullable().optional(),
  dataAvailability: z.string().max(20_000).nullable().optional(),
  codeAvailability: z.string().max(20_000).nullable().optional(),
  acknowledgements: z.string().max(20_000).nullable().optional(),
  contributions: z.string().max(20_000).nullable().optional(),
  checklist: z
    .array(z.object({ id: z.string().max(50), label: z.string().max(500), done: z.boolean(), date: zDateOnly.nullable().optional() }))
    .max(200)
    .optional(),
});

export const ManuscriptStatusInput = z.object({
  status: z.enum(MANUSCRIPT_STATUS.values),
  date: zDateOnly,
  note: z.string().max(5000).optional(),
});

export const RecordSubmissionInput = z.object({
  venue: z.string().trim().min(1).max(500),
  submittedOn: zDateOnly,
  identifiers: z.string().max(2000).nullable().optional(),
  notes: z.string().max(10_000).nullable().optional(),
  acknowledgedIssueIds: z.array(z.string().max(200)).default([]),
  updateStatus: z.boolean().default(true),
});

export const ReviewerCommentsInput = z.object({
  reviewerLabel: z.string().trim().min(1).max(200),
  receivedOn: zDateOnly.nullable().optional(),
  originalText: z.string().min(1).max(500_000),
  submissionId: zId.nullable().optional(),
  /** sectionId: a section chosen by the person; null = a general comment; left out = the app guesses. */
  items: z.array(z.object({ requestText: z.string().min(1).max(50_000), sectionId: zId.nullable().optional() })).min(1).max(500),
});

export const UpdateRevisionItemInput = z.object({
  version: z.number().int(),
  sectionId: zId.nullable().optional(),
  proposedChange: z.string().max(50_000).optional(),
  changeStatus: z.enum(REVISION_CHANGE_STATUS.values).optional(),
  confirmedChange: z.string().max(50_000).nullable().optional(),
  responseDraft: z.string().max(50_000).optional(),
});

export const BriefInput = z.object({
  fields: z.object({
    questions: z.array(z.object({ text: z.string().trim().min(1).max(2000), recordId: zId.nullable().optional() })).max(50),
    motivation: z.string().max(20_000),
    contribution: z.string().max(20_000),
    scope: z.string().max(20_000),
    constraints: z.string().max(20_000),
    targetOutputs: z.string().max(20_000),
    successCriteria: z.string().max(20_000),
    proposalNoteId: zId.nullable().optional(),
  }),
  changeNote: z.string().max(2000).optional(),
});

export const CommentInput = z.object({
  body: z.string().trim().min(1).max(20_000),
  anchorQuote: z.string().max(2000).nullable().optional(),
});

export const TemplateUpdateInput = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  bodyMd: z.string().max(200_000).optional(),
});

export const PrefInput = z.object({
  scope: z.string().max(100),
  key: z.string().max(100),
  value: z.unknown(),
});
